// Real Data Service - Integrates multiple public APIs for realistic GTM data

interface RealUser {
  id: number;
  name: {
    first: string;
    last: string;
  };
  email: string;
  phone: string;
  picture: {
    large: string;
    medium: string;
    thumbnail: string;
  };
  location: {
    city: string;
    state: string;
    country: string;
  };
  nat: string;
}

interface JSONPlaceholderUser {
  id: number;
  name: string;
  username: string;
  email: string;
  phone: string;
  website: string;
  company: {
    name: string;
    catchPhrase: string;
    bs: string;
  };
  address: {
    city: string;
    zipcode: string;
  };
}

interface EnrichedLead {
  id: string;
  name: string;
  company: string;
  email: string;
  phone: string;
  status: 'New' | 'Contacted' | 'Qualified' | 'Lost';
  source: string;
  value: number;
  createdAt: string;
  avatar?: string;
  location?: string;
}

interface RevenueData {
  date: string;
  amount: number;
  transactions: number;
}

interface ActivityEvent {
  user: string;
  action: string;
  time: string;
  amount?: string;
  avatar: string;
  color: string;
}

class RealDataService {
  private baseUrls = {
    randomUser: 'https://randomuser.me/api/',
    jsonPlaceholder: 'https://jsonplaceholder.typicode.com',
    cryptoCompare: 'https://min-api.cryptocompare.com/data', // For realistic financial data
    quotable: 'https://api.quotable.io', // For company catchphrases
  };

  // Cache for reducing API calls - Reduced cache time to ensure AI gets fresh data
  private cache: Map<string, { data: any; timestamp: number }> = new Map();
  private readonly CACHE_DURATION = 1 * 60 * 1000; // 1 minute - reduced for AI consistency

  private getCachedData<T>(key: string): T | null {
    const cached = this.cache.get(key);
    if (cached && Date.now() - cached.timestamp < this.CACHE_DURATION) {
      return cached.data;
    }
    return null;
  }

  private setCachedData(key: string, data: any): void {
    this.cache.set(key, { data, timestamp: Date.now() });
  }

  // Generate realistic leads using Random User API + JSONPlaceholder
  async generateRealLeads(count: number = 20): Promise<EnrichedLead[]> {
    const cacheKey = `leads-${count}`;
    const cached = this.getCachedData<EnrichedLead[]>(cacheKey);
    if (cached) return cached;

    try {
      const [randomUsers, jsonUsers] = await Promise.all([
        fetch(`${this.baseUrls.randomUser}?results=${Math.ceil(count / 2)}&nat=us,gb,ca,au`).then(r => r.json()),
        fetch(`${this.baseUrls.jsonPlaceholder}/users`).then(r => r.json())
      ]);

      const leads: EnrichedLead[] = [];
      const statuses: EnrichedLead['status'][] = ['New', 'Contacted', 'Qualified', 'Lost'];
      const sources = ['Website', 'LinkedIn', 'Referral', 'Cold Email', 'Trade Show', 'Partner', 'Webinar'];

      // Process Random User API data
      randomUsers.results?.forEach((user: RealUser, index: number) => {
        leads.push({
          id: `real-${typeof user.id === 'object' ? index : user.id || index}`,
          name: `${user.name.first} ${user.name.last}`,
          company: this.generateCompanyName(),
          email: user.email,
          phone: user.phone,
          status: statuses[Math.floor(Math.random() * statuses.length)],
          source: sources[Math.floor(Math.random() * sources.length)],
          value: Math.floor(Math.random() * 150000) + 10000,
          createdAt: this.generateRecentDate(),
          avatar: user.picture.thumbnail,
          location: `${user.location.city}, ${user.location.state}`,
        });
      });

      // Process JSONPlaceholder data (mix it in)
      jsonUsers.slice(0, Math.floor(count / 2)).forEach((user: JSONPlaceholderUser) => {
        leads.push({
          id: `jp-${user.id}`,
          name: user.name,
          company: user.company.name,
          email: user.email,
          phone: user.phone.split(' ')[0], // Clean up phone format
          status: statuses[Math.floor(Math.random() * statuses.length)],
          source: sources[Math.floor(Math.random() * sources.length)],
          value: Math.floor(Math.random() * 200000) + 5000,
          createdAt: this.generateRecentDate(),
          location: user.address.city,
        });
      });

      const shuffledLeads = leads.sort(() => Math.random() - 0.5).slice(0, count);
      this.setCachedData(cacheKey, shuffledLeads);
      return shuffledLeads;

    } catch (error) {
      console.error('Error fetching real lead data:', error);
      return this.generateFallbackLeads(count);
    }
  }

  // Generate realistic revenue data
  async generateRevenueData(days: number = 30): Promise<RevenueData[]> {
    const cacheKey = `revenue-${days}`;
    const cached = this.getCachedData<RevenueData[]>(cacheKey);
    if (cached) return cached;

    const revenueData: RevenueData[] = [];
    const baseAmount = 50000;

    for (let i = days - 1; i >= 0; i--) {
      const date = new Date();
      date.setDate(date.getDate() - i);

      // Add some realistic variance (seasonal patterns, weekends, etc.)
      const isWeekend = date.getDay() === 0 || date.getDay() === 6;
      const seasonalMultiplier = 0.8 + (Math.sin(i / 30 * Math.PI) * 0.3);
      const weekendPenalty = isWeekend ? 0.6 : 1.0;
      const randomVariance = 0.7 + (Math.random() * 0.6);

      const amount = Math.floor(baseAmount * seasonalMultiplier * weekendPenalty * randomVariance);
      const transactions = Math.floor((amount / 1000) + (Math.random() * 20));

      revenueData.push({
        date: date.toISOString().split('T')[0],
        amount,
        transactions,
      });
    }

    this.setCachedData(cacheKey, revenueData);
    return revenueData;
  }

  // Generate realistic activity feed using real user data
  async generateActivityFeed(count: number = 10): Promise<ActivityEvent[]> {
    const cacheKey = `activity-${count}`;
    const cached = this.getCachedData<ActivityEvent[]>(cacheKey);
    if (cached) return cached;

    try {
      const response = await fetch(`${this.baseUrls.randomUser}?results=${count}&nat=us,gb,ca`);
      const userData = await response.json();

      const actions = [
        'closed deal', 'added new opportunity', 'updated opportunity stage',
        'scheduled demo', 'sent proposal', 'logged call', 'qualified lead',
        'sent follow-up', 'created contact', 'updated forecast'
      ];

      const dealTypes = [
        'Enterprise Software', 'Cloud Migration', 'Security Consultation',
        'Data Analytics Platform', 'API Integration', 'Digital Transformation',
        'DevOps Solution', 'Compliance Package', 'Training Program'
      ];

      const activities: ActivityEvent[] = userData.results.map((user: RealUser, index: number) => {
        const action = actions[Math.floor(Math.random() * actions.length)];
        const dealType = dealTypes[Math.floor(Math.random() * dealTypes.length)];
        const hours = Math.floor(Math.random() * 24) + 1;
        const amount = Math.floor(Math.random() * 100000) + 5000;

        return {
          user: `${user.name.first} ${user.name.last}`,
          action: `${action} "${dealType} - ${Math.floor(Math.random() * 99) + 1}"`,
          time: `${hours} hour${hours > 1 ? 's' : ''} ago`,
          amount: `$${amount.toLocaleString()}`,
          avatar: user.name.first[0] + user.name.last[0],
          color: this.getRandomColor(),
        };
      });

      this.setCachedData(cacheKey, activities);
      return activities;

    } catch (error) {
      console.error('Error generating activity feed:', error);
      return this.generateFallbackActivity(count);
    }
  }

  // Get real company performance data (simulated but realistic)
  async getCompanyMetrics(): Promise<any> {
    const cacheKey = 'company-metrics';
    const cached = this.getCachedData(cacheKey);
    if (cached) return cached;

    // Simulate fetching from multiple GTM tools
    const metrics = {
      totalRevenue: Math.floor(Math.random() * 2000000) + 500000,
      monthlyGrowth: (Math.random() * 20) + 5, // 5-25% growth
      activeDeals: Math.floor(Math.random() * 50) + 20,
      conversionRate: Math.floor((Math.random() * 15) + 10), // 10-25%
      avgDealSize: Math.floor(Math.random() * 50000) + 25000,
      salesCycleLength: Math.floor(Math.random() * 30) + 45, // 45-75 days
      lastUpdated: new Date().toISOString(),
    };

    this.setCachedData(cacheKey, metrics);
    return metrics;
  }

  // Helper methods
  private generateCompanyName(): string {
    const adjectives = ['Global', 'Advanced', 'Dynamic', 'Strategic', 'Digital', 'Smart', 'Innovative', 'Premier'];
    const nouns = ['Solutions', 'Systems', 'Technologies', 'Enterprises', 'Industries', 'Corp', 'Group', 'Labs'];

    return `${adjectives[Math.floor(Math.random() * adjectives.length)]} ${nouns[Math.floor(Math.random() * nouns.length)]}`;
  }

  private generateRecentDate(): string {
    const daysAgo = Math.floor(Math.random() * 30);
    const date = new Date();
    date.setDate(date.getDate() - daysAgo);
    return date.toISOString().split('T')[0];
  }

  private getRandomColor(): string {
    const colors = [
      'bg-blue-500', 'bg-green-500', 'bg-purple-500', 'bg-orange-500',
      'bg-pink-500', 'bg-indigo-500', 'bg-teal-500', 'bg-red-500'
    ];
    return colors[Math.floor(Math.random() * colors.length)];
  }

  // Fallback methods when APIs fail
  private generateFallbackLeads(count: number): EnrichedLead[] {
    const leads: EnrichedLead[] = [];
    const names = ['John Smith', 'Sarah Johnson', 'Michael Brown', 'Emily Davis', 'David Wilson'];
    const companies = ['Acme Corp', 'Tech Solutions', 'Global Industries', 'Digital Ventures', 'Innovation Labs'];

    for (let i = 0; i < count; i++) {
      leads.push({
        id: `fallback-${i}`,
        name: names[i % names.length],
        company: companies[i % companies.length],
        email: `user${i}@example.com`,
        phone: `+1 (555) ${Math.floor(Math.random() * 900) + 100}-${Math.floor(Math.random() * 9000) + 1000}`,
        status: ['New', 'Contacted', 'Qualified', 'Lost'][i % 4] as any,
        source: 'Website',
        value: Math.floor(Math.random() * 100000) + 10000,
        createdAt: this.generateRecentDate(),
      });
    }

    return leads;
  }

  private generateFallbackActivity(count: number): ActivityEvent[] {
    const activities: ActivityEvent[] = [];
    const users = ['Alice Johnson', 'Bob Smith', 'Carol Williams'];

    for (let i = 0; i < count; i++) {
      activities.push({
        user: users[i % users.length],
        action: 'updated opportunity',
        time: `${i + 1} hours ago`,
        amount: `$${(Math.floor(Math.random() * 50000) + 10000).toLocaleString()}`,
        avatar: users[i % users.length].split(' ').map(n => n[0]).join(''),
        color: this.getRandomColor(),
      });
    }

    return activities;
  }

  // Clear cache (useful for development)
  clearCache(): void {
    this.cache.clear();
  }
}

export const realDataService = new RealDataService();
export default realDataService;